import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Send, 
  Twitter, 
  Music2, 
  Calendar as CalendarIcon, 
  Clock,
  Image,
  X,
  Zap,
  AlertCircle,
  Check,
  Sparkles,
  Wand2,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_INFO = {
  telegram: { name: 'Telegram', icon: Send, color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  twitter: { name: 'Twitter', icon: Twitter, color: 'text-sky-400', bgColor: 'bg-sky-500/10' },
  tiktok: { name: 'TikTok', icon: Music2, color: 'text-pink-400', bgColor: 'bg-pink-500/10' },
};

const CreatePost = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('12:00');
  const [isScheduling, setIsScheduling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  
  // AI Caption generation state
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [captionStyle, setCaptionStyle] = useState('casual');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [selectedMediaForCaption, setSelectedMediaForCaption] = useState(null);

  useEffect(() => {
    fetchConnectedAccounts();
  }, []);

  const fetchConnectedAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setConnectedAccounts(response.data);
    } catch (error) {
    }
  };

  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId) 
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (mediaFiles.length + files.length > 4) {
      toast.error('Maximum 4 media files allowed');
      return;
    }

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMediaFiles(prev => [...prev, { ...response.data, preview: URL.createObjectURL(file) }]);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const removeMedia = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openCaptionGenerator = (media) => {
    setSelectedMediaForCaption(media);
    setCaptionStyle('casual');
    setCustomPrompt('');
    setShowCaptionModal(true);
  };

  const generateCaption = async () => {
    if (!selectedMediaForCaption) {
      toast.error('Please select an image first');
      return;
    }

    setGeneratingCaption(true);
    try {
      const response = await axios.post(`${API}/generate-caption`, {
        media_id: selectedMediaForCaption.id,
        style: captionStyle,
        custom_prompt: customPrompt || undefined
      });

      if (response.data.success) {
        // Append or replace content with generated caption
        const newCaption = response.data.caption;
        if (content.trim()) {
          // Ask user if they want to replace or append
          setContent(prev => prev + '\n\n' + newCaption);
          toast.success('Caption added to your content!');
        } else {
          setContent(newCaption);
          toast.success('Caption generated successfully!');
        }
        setShowCaptionModal(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate caption';
      toast.error(message);
    } finally {
      setGeneratingCaption(false);
    }
  };

  const handleSubmit = async (publish = false) => {
    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }

    if (selectedAccounts.length === 0) {
      toast.error('Please select at least one account to post to');
      return;
    }

    setLoading(true);

    try {
      let scheduledAt = null;
      if (isScheduling && scheduleDate) {
        const [hours, minutes] = scheduleTime.split(':');
        const scheduled = new Date(scheduleDate);
        scheduled.setHours(parseInt(hours), parseInt(minutes));
        scheduledAt = scheduled.toISOString();
      }

      // Get unique platforms from selected accounts
      const selectedPlatforms = [...new Set(
        connectedAccounts
          .filter(acc => selectedAccounts.includes(acc.id))
          .map(acc => acc.platform)
      )];

      const postData = {
        content,
        platforms: selectedPlatforms,
        scheduled_at: scheduledAt,
        media_urls: mediaFiles.map(m => m.url),
        account_ids: selectedAccounts
      };

      const response = await axios.post(`${API}/posts`, postData);

      if (publish && !scheduledAt) {
        // Publish immediately
        await axios.post(`${API}/posts/${response.data.id}/publish`);
        toast.success('Post published successfully!');
      } else if (scheduledAt) {
        toast.success('Post scheduled successfully!');
      } else {
        toast.success('Post saved as draft!');
      }

      navigate('/posts');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create post';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const characterCount = content.length;
  
  // Platform-specific character limits
  const PLATFORM_LIMITS = {
    twitter: { limit: 4000, name: 'Twitter (Blue)' },  // Twitter Blue limit
    telegram: { limit: 4096, name: 'Telegram' },
    facebook: { limit: 63206, name: 'Facebook' },
    instagram: { limit: 2200, name: 'Instagram' }
  };
  
  // Get the most restrictive limit based on selected accounts
  const getRelevantLimit = () => {
    if (selectedAccounts.length === 0) {
      return { limit: 4000, name: 'Default' }; // Default to Twitter Blue limit
    }
    
    const selectedPlatforms = selectedAccounts.map(id => {
      const account = connectedAccounts.find(a => a.id === id);
      return account?.platform;
    }).filter(Boolean);
    
    // Find the most restrictive limit among selected platforms
    let minLimit = 63206;
    let limitName = '';
    
    for (const platform of selectedPlatforms) {
      const platformConfig = PLATFORM_LIMITS[platform];
      if (platformConfig && platformConfig.limit < minLimit) {
        minLimit = platformConfig.limit;
        limitName = platformConfig.name;
      }
    }
    
    return { limit: minLimit, name: limitName };
  };
  
  const relevantLimit = getRelevantLimit();
  const maxCharacters = relevantLimit.limit;

  // Group accounts by platform
  const accountsByPlatform = connectedAccounts.reduce((acc, account) => {
    if (!acc[account.platform]) {
      acc[account.platform] = [];
    }
    acc[account.platform].push(account);
    return acc;
  }, {});

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="create-post-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create Post</h1>
        <p className="text-muted-foreground">Compose your content and publish to multiple accounts.</p>
      </div>

      {/* Content Editor */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Content</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Textarea
              placeholder="What's on your mind? Write your post here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[150px] bg-background border-border/50 focus:border-primary resize-none"
              data-testid="post-content-input"
            />
            <div className="flex justify-between items-center mt-2">
              <span className={cn(
                "text-xs font-mono",
                characterCount > maxCharacters ? "text-destructive" : "text-muted-foreground"
              )}>
                {characterCount}/{maxCharacters}
                {relevantLimit.name && <span className="ml-1 text-gray-500">({relevantLimit.name})</span>}
              </span>
              {characterCount > maxCharacters && (
                <span className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Content exceeds {relevantLimit.name} limit
                </span>
              )}
            </div>
          </div>

          {/* Media Upload */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Media (optional)</Label>
            <div className="flex flex-wrap gap-2">
              {mediaFiles.map((media, index) => (
                <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted group">
                  <img src={media.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeMedia(index)}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center hover:bg-black z-10"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  {/* AI Caption button - only for images */}
                  {media.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                    <button
                      onClick={() => openCaptionGenerator(media)}
                      className="absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-1 bg-purple-600/90 hover:bg-purple-600 rounded text-[10px] font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Generate AI Caption"
                    >
                      <Sparkles className="w-3 h-3" />
                      AI
                    </button>
                  )}
                </div>
              ))}
              {mediaFiles.length < 4 && (
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center cursor-pointer transition-colors">
                  <Image className="w-6 h-6 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleMediaUpload}
                    className="hidden"
                    data-testid="media-upload-input"
                  />
                </label>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Selection */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Select Accounts</CardTitle>
          <p className="text-sm text-muted-foreground">Choose which accounts to publish to</p>
        </CardHeader>
        <CardContent>
          {Object.keys(accountsByPlatform).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(accountsByPlatform).map(([platform, accounts]) => {
                const platformInfo = PLATFORM_INFO[platform];
                const Icon = platformInfo?.icon || Send;
                
                return (
                  <div key={platform} className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Icon className={cn("w-4 h-4", platformInfo?.color)} />
                      {platformInfo?.name || platform}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                      {accounts.map((account) => {
                        const isSelected = selectedAccounts.includes(account.id);
                        return (
                          <div
                            key={account.id}
                            onClick={() => toggleAccount(account.id)}
                            className={cn(
                              "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                              isSelected 
                                ? "border-primary bg-primary/10" 
                                : "border-border/50 hover:border-border"
                            )}
                            data-testid={`account-${account.id}-btn`}
                          >
                            <Checkbox 
                              checked={isSelected}
                              className="pointer-events-none"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{account.account_name}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No accounts connected yet</p>
              <p className="text-xs text-muted-foreground">
                Go to Connected Accounts to add your social media accounts
              </p>
            </div>
          )}
          
          {selectedAccounts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-sm text-muted-foreground">
                <span className="text-primary font-medium">{selectedAccounts.length}</span> account{selectedAccounts.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scheduling */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Schedule</CardTitle>
            <button
              onClick={() => setIsScheduling(!isScheduling)}
              className={cn(
                "px-3 py-1 rounded-full text-sm transition-colors",
                isScheduling 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
              data-testid="schedule-toggle-btn"
            >
              {isScheduling ? 'Scheduled' : 'Not Scheduled'}
            </button>
          </div>
        </CardHeader>
        {isScheduling && (
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label className="text-sm text-muted-foreground mb-2 block">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-background border-border/50"
                      data-testid="schedule-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {scheduleDate ? format(scheduleDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={scheduleDate}
                      onSelect={setScheduleDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="w-full sm:w-32">
                <Label className="text-sm text-muted-foreground mb-2 block">Time</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 rounded-md border border-border/50 bg-background text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="schedule-time-input"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          className="flex-1 border-border/50"
          onClick={() => handleSubmit(false)}
          disabled={loading}
          data-testid="save-draft-btn"
        >
          Save as Draft
        </Button>
        <Button
          className="flex-1 glow-primary"
          onClick={() => handleSubmit(true)}
          disabled={loading || selectedAccounts.length === 0}
          data-testid="publish-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Processing...
            </span>
          ) : isScheduling && scheduleDate ? (
            <>
              <CalendarIcon className="w-4 h-4 mr-2" />
              Schedule Post
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Publish Now
            </>
          )}
        </Button>
      </div>

      {/* AI Caption Generation Modal */}
      <Dialog open={showCaptionModal} onOpenChange={setShowCaptionModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-400" />
              Generate AI Caption
            </DialogTitle>
            <DialogDescription>
              Let AI analyze your image and create an engaging caption
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Preview selected image */}
            {selectedMediaForCaption && (
              <div className="flex justify-center">
                <img 
                  src={selectedMediaForCaption.preview} 
                  alt="Selected" 
                  className="w-32 h-32 object-cover rounded-lg border border-gray-600"
                />
              </div>
            )}
            
            {/* Style selection */}
            <div className="space-y-2">
              <Label className="text-white">Caption Style</Label>
              <Select value={captionStyle} onValueChange={setCaptionStyle}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select style..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="casual" className="text-white hover:bg-gray-700">😊 Casual & Friendly</SelectItem>
                  <SelectItem value="professional" className="text-white hover:bg-gray-700">💼 Professional</SelectItem>
                  <SelectItem value="funny" className="text-white hover:bg-gray-700">😂 Funny & Witty</SelectItem>
                  <SelectItem value="harry_potter" className="text-white hover:bg-gray-700">🧙 Harry Potter Style</SelectItem>
                  <SelectItem value="sports_hype" className="text-white hover:bg-gray-700">🏆 Sports Hype</SelectItem>
                  <SelectItem value="crypto" className="text-white hover:bg-gray-700">🚀 Crypto/Trading</SelectItem>
                  <SelectItem value="casino" className="text-white hover:bg-gray-700">🎰 Casino/Gaming</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom instructions */}
            <div className="space-y-2">
              <Label className="text-white">Custom Instructions (optional)</Label>
              <Input
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="e.g., Include a call to action, mention our brand..."
                className="bg-gray-800 border-gray-600 text-white"
              />
              <p className="text-xs text-gray-400">Add specific requirements for your caption</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCaptionModal(false)} className="border-gray-600">
              Cancel
            </Button>
            <Button 
              onClick={generateCaption} 
              disabled={generatingCaption}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generatingCaption ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Caption
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatePost;
